import { useCallback, useEffect, useState } from 'react';
import {
  AutoComplete,
  Col,
  Divider,
  Flex,
  Form,
  message,
  Row,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import {
  AddItemButton,
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomParagraph,
  CustomSelect,
  CustomTitle,
  HighlightedText,
  ToggleSwitch,
} from '../../components';
import { getPermissions, useToken } from '../../common/helper';
import { hideFdaDatabaseForCompany } from '../../common/constants';
import { useSpin } from '../../components/SpinContext';
import { AddProducts, GetProductById } from '../../apis/onboarding';
import { MIN_ABOVE_MAX_MESSAGE, isMinAboveMax } from './productRangeValidation';
import {
  getProductAutocomplete,
  getProductFDAInfo,
  getProductAutofillInfo,
} from '../../apis/products';
import {
  getProductsTypeDDL,
  getTeamsDDLOFFeature,
} from '../../common/utils/dropDown';
import { determineTemperatureRange } from '../../common/utils/temperatureUtils';
import TeamsSelector from '../../components/TeamsSelector';
import {
  AssignPermissionOptions,
  tempratureRangeList,
} from '../../common/static_data';
import { DeleteIcon } from '@/assets/icons/icons';
import styles from './SettingsProductDrawerForm.module.scss';
import { useSelector } from 'react-redux';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import TimeOutOfRangeDrawer, {
  buildTimeOutOfRangeFormData,
} from './TimeOutOfRangeDrawer';
import {
  GetProductTimeOutOfRange,
  SaveProductTimeOutOfRange,
} from '@/apis/settings';

const REQUIREMENT_KEYS = ['shock', 'light', 'tilt', 'pressure', 'humidity'];

// TOR's own (and only) prerequisite — kept as one constant so the disabled
// tooltip, toggle tooltip, and the Edit click's error toast can't drift
// out of sync with each other or with hasRequiredFieldsFilled's actual check.
const TOR_REQUIRED_FIELD_MESSAGE = 'Temperature range required';

const getTimeOutOfRangePayload = (res) =>
  res?.data?.data?.data || res?.data?.data || res?.data || null;

// TODO: replace with a real feature-flag/permission check once the
// Temperature GxP feature has a backend-driven toggle per company.

const SettingsProductDrawerForm = ({
  form,
  id,
  handleCloseDrawer,
  handleFetchAllProduct,
  showBackButton = false,
}) => {
  const { token, removeToken, decodedUserToken } = useToken();
  const hideFdaDatabase = hideFdaDatabaseForCompany(
    decodedUserToken()?.company_id
  );
  const decodedToken = decodedUserToken();
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const [_permissionOptions, _setPermissionOptions] = useState(
    AssignPermissionOptions
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const features = useSelector((state) => state.menu.features);

  const TOR_rule_permissons = features?.find(
    (feature) => feature?.name === 'TOR rules'
  );

  const main_menu = useSelector((state) => state.menu.items);
  const GxP_permissions = getPermissions(main_menu, 'Settings', 'GxP reports');

  // Available for both add and edit now — only the data-fetch below stays
  // gated on `id`, since a new product has no saved TOR data yet.
  const TEMPERATURE_GXP_ENABLED =
    GxP_permissions?.is_view &&
    TOR_rule_permissons?.permissions?.is_view &&
    Boolean(decodedToken?.temperature_gxp_enabled);

  //get user by Id
  const [selectedTeams, setSelectedTeams] = useState([]);

  const unauthorize = useCallback(
    (error) => {
      if (error && error?.status === 401) {
        removeToken();
        navigate('/login');
      }
    },
    [removeToken, navigate]
  );

  const [isCustomRange, setIsCustomRange] = useState(false);
  const [productTypeDDL, setProductTypeDDL] = useState([]);
  const [_isCommercial, _setIsCommercail] = useState(false);

  // FDA Data state
  const [showFDAData, setShowFDAData] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [actualSelectedProductName, setActualSelectedProductName] =
    useState('');
  const [fdaData, setFdaData] = useState(null);
  const [fdaLoading, setFdaLoading] = useState(false);

  // Autofill state
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillData, setAutofillData] = useState(null);

  const [temperatureObject, setTemperatureObject] = useState({
    temperature_max: '',
    temperature_min: '',
    temp_range_is_custom: false,
    temperature_unit: 'C',
  });

  // Time out of range (Temperature GxP) — the drawer owns its own GET/POST
  // calls (see apis/onboarding.ts); this form only tracks the toggle shown
  // on the product row and whether the sub-drawer is open.
  const [timeOutOfRangeEnabled, setTimeOutOfRangeEnabled] = useState(false);
  const [showTimeOutOfRangeDrawer, setShowTimeOutOfRangeDrawer] =
    useState(false);
  // reopening the drawer restores them, and used to build the FormData once
  // the product is actually created.
  const [pendingTimeOutOfRangeData, setPendingTimeOutOfRangeData] =
    useState(null);

  const teamsList = useSelector((state) => state.teams.list);

  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  // Live read of the temperature range so the "Edit" tooltip in the add case
  // can drop the moment it's actually filled in, instead of staying tied
  // to add/edit mode.
  const watchedTemperatureRange = Form.useWatch('temperature_range', form);
  const watchedTemperatureMin = Form.useWatch('temperature_min', form);
  const watchedTemperatureMax = Form.useWatch('temperature_max', form);

  // TOR only needs a temperature range configured (a custom range also
  // needs min/max) — it monitors excursions against that profile, so
  // name/product type/teams have no bearing on whether it can be enabled.
  const hasRequiredFieldsFilled = Boolean(
    watchedTemperatureRange &&
    (watchedTemperatureRange !== 'custom' ||
      (watchedTemperatureMin !== '' &&
        watchedTemperatureMin !== null &&
        watchedTemperatureMin !== undefined &&
        watchedTemperatureMax !== '' &&
        watchedTemperatureMax !== null &&
        watchedTemperatureMax !== undefined))
  );

  // Edit mode always has these fields already saved; add mode needs them
  // filled in first before the toggle/Edit link can be used.
  const canEnableTimeOutOfRange = Boolean(id) || hasRequiredFieldsFilled;

  const [allTeams, setAllTeams] = useState([]);

  // Product autocomplete state
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [useAutocomplete, setUseAutocomplete] = useState(false);
  const fdaAutocompleteEnabled = useAutocomplete && !hideFdaDatabase;

  useEffect(() => {
    getProductsTypeDDL(token).then((val) => {
      if (val && val.length > 0) {
        setProductTypeDDL(val);
      }
    });
    getTeamsDDLOFFeature(token, 'Products').then((val) => {
      if (val && val.length > 0) {
        setAllTeams(val);
      }
    });
  }, [token]);

  //get product by Id
  const GetSingleProductById = useCallback(
    (id) => {
      showSpin();
      GetProductById(token, id)
        .then((res) => {
          const data = res?.data ? res.data : null;
          if (data) {
            if (data?.data?.is_success) {
              let resp = data?.data?.data ? data?.data?.data : null;
              if (resp) {
                if (
                  resp?.stage_of_development?.toLowerCase() === 'commercial'
                ) {
                  _setIsCommercail(true);
                }
                form.setFieldsValue({
                  name: resp.name || '',
                  id: resp.id || null,
                  product_type: resp.product_type || '',
                  identifier: resp.identifier || '',
                  stage_of_development: resp.stage_of_development || '',
                  commercial_value: resp.commercial_value || '',
                  project_id: resp.project_id || '',
                  temperature_range: resp.temperature_range || null,
                  viability_window: resp.viability_window || null,
                  temperature_min: resp.temperature_min || '',
                  temperature_max: resp.temperature_max || '',
                  temp_range_is_custom: resp.temp_range_is_custom || false,
                  temperature_unit: resp.temperature_unit || '',
                  teams: resp?.teams || [],
                  // add more handling requirements
                  shock: resp.shock ?? '',
                  light: resp.light ?? '',
                  tilt: resp.tilt ?? '',
                  pressure_min: resp.pressure_min ?? '',
                  pressure_max: resp.pressure_max ?? '',
                  pressure_unit: resp.pressure_unit || '',
                  humidity_min: resp.humidity_min ?? '',
                  humidity_max: resp.humidity_max ?? '',
                });
                // Determine temperature range based on min/max values
                let tempRangeValue = resp.temperature_range || 'custom';
                let isCustomTemp = resp.temp_range_is_custom || false;

                // If we have min/max values, determine the temperature range
                if (
                  resp.temperature_min !== null &&
                  resp.temperature_min !== undefined &&
                  resp.temperature_max !== null &&
                  resp.temperature_max !== undefined
                ) {
                  const rangeInfo = determineTemperatureRange(
                    resp.temperature_min,
                    resp.temperature_max,
                    resp.temperature_unit || 'C'
                  );

                  if (rangeInfo.type === 'standard') {
                    tempRangeValue = rangeInfo.value;
                    isCustomTemp = false;
                  } else if (rangeInfo.type === 'not_specified') {
                    tempRangeValue = 'none';
                    isCustomTemp = false;
                  } else {
                    tempRangeValue = 'custom';
                    isCustomTemp = true;
                  }
                }

                // Update the form with the determined temperature range
                form.setFieldsValue({
                  temperature_range: tempRangeValue,
                  temp_range_is_custom: isCustomTemp,
                });

                setTemperatureObject({
                  temperature_min: resp.temperature_min,
                  temperature_max: resp.temperature_max,
                  temperature_unit: resp.temperature_unit || 'C',
                  temp_range_is_custom: isCustomTemp,
                });
                setIsCustomRange(isCustomTemp);

                const selectedFromBackend = REQUIREMENT_KEYS.filter((key) => {
                  if (key === 'pressure') {
                    return (
                      (resp.pressure_min !== undefined &&
                        resp.pressure_min !== null &&
                        resp.pressure_min !== '') ||
                      (resp.pressure_max !== undefined &&
                        resp.pressure_max !== null &&
                        resp.pressure_max !== '')
                    );
                  }

                  if (key === 'humidity') {
                    return (
                      (resp.humidity_min !== undefined &&
                        resp.humidity_min !== null &&
                        resp.humidity_min !== '') ||
                      (resp.humidity_max !== undefined &&
                        resp.humidity_max !== null &&
                        resp.humidity_max !== '')
                    );
                  }

                  return (
                    resp[key] !== undefined &&
                    resp[key] !== null &&
                    resp[key] !== ''
                  );
                });

                const selectedCapitalized = selectedFromBackend.map(
                  (key) => key.charAt(0).toUpperCase() + key.slice(1)
                );
                setSelectedRequirements(selectedCapitalized);
                setShowDropdown(selectedCapitalized.length === 5);
                setAvailableOptions((prevOptions) =>
                  prevOptions.filter(
                    (opt) => !selectedCapitalized.includes(opt)
                  )
                );

                setIsCustomRange(resp.temp_range_is_custom);
                setSelectedTeams(resp?.teams || []);
                setSelectedProductName(resp.name || '');
                setActualSelectedProductName(resp.name || '');
              }
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [token, showSpin, hideSpin, unauthorize, form]
  );

  const _handleIsCommercial = (value) => {
    _setIsCommercail(false);
    if (value && value === 'Commercial') {
      _setIsCommercail(true);
    }
  };

  useEffect(() => {
    if (id) {
      GetSingleProductById(id);
    }
  }, [id, GetSingleProductById]);
  const [timeOutOfRangeData, setTimeOutOfRangeData] = useState(null);

  useEffect(() => {
    if (!TEMPERATURE_GXP_ENABLED) return;

    if (!id) {
      setTimeOutOfRangeEnabled(false);
      setTimeOutOfRangeData(null);
      return;
    }
    GetProductTimeOutOfRange(token, id)
      .then((res) => {
        const data = getTimeOutOfRangePayload(res);

        setTimeOutOfRangeData(data);
        setTimeOutOfRangeEnabled(!!data?.is_enabled);
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [id, TEMPERATURE_GXP_ENABLED, token, unauthorize]);

  const handleTemperatureRangeChange = (e) => {
    if (e) {
      if (e === 'custom') {
        form.setFieldsValue({
          temp_range_is_custom: true,
          temperature_min: '',
          temperature_max: '',
          temperature_unit: 'C',
        });
        setIsCustomRange(true);
      } else if (e === 'none') {
        form.setFieldsValue({
          temp_range_is_custom: false,
          temperature_min: null,
          temperature_max: null,
          temperature_unit: 'C',
        });
        setTemperatureObject({
          temperature_min: null,
          temperature_max: null,
          temperature_unit: 'C',
          temp_range_is_custom: false,
        });
        setIsCustomRange(false);
      } else {
        let obj = tempratureRangeList.find((x) => x.value === e);
        if (obj) {
          form.setFieldsValue({
            temperature_min: obj.min,
            temperature_max: obj.max,
            temperature_unit: 'C',
            temp_range_is_custom: false,
          });
          setTemperatureObject({
            temperature_min: obj.min,
            temperature_max: obj.max,
            temperature_unit: 'C',
            temp_range_is_custom: false,
          });
          setIsCustomRange(false);
        }
      }
    }
  };

  const onFinish = (values) => {
    if (values && Object.keys(values).length > 0) {
      let model = form.getFieldsValue();

      // Form is converting to uppercase, override with our state value
      if (fdaAutocompleteEnabled && selectedProductName) {
        model.name = selectedProductName;
      }

      // Validate product_type exists
      if (!model.product_type || model.product_type === '') {
        message.error('Please select a Product Type before saving.');
        return;
      }

      // Convert product_type name to UUID if needed
      if (
        model.product_type &&
        typeof model.product_type === 'string' &&
        !model.product_type.includes('-')
      ) {
        const matchingType = productTypeDDL.find(
          (type) => type.name === model.product_type
        );
        if (matchingType) {
          model.product_type = matchingType.id;
        } else {
          message.error(
            'Invalid product type selected. Please select from the dropdown.'
          );
          return;
        }
      }

      if (
        ('humidity_min' in model || 'humidity_max' in model) &&
        (model?.humidity_min == null || model?.humidity_min === '') &&
        (model?.humidity_max == null || model?.humidity_max === '')
      ) {
        message.error(
          'Humidity requirement cannot be left empty. Either remove it or add a value.'
        );
        return;
      }
      //delete model?.requirement;

      if (
        ('pressure_min' in model || 'pressure_max' in model) &&
        (model?.pressure_min == null || model?.pressure_min === '') &&
        (model?.pressure_max == null || model?.pressure_max === '')
      ) {
        message.error(
          'Pressures requirement cannot be left empty. Either remove it or add a value.'
        );
        return;
      }

      model.time_out_of_range_enabled = timeOutOfRangeEnabled;
      model.id = id ? id : null;
      const teamArray = selectedTeams
        ? selectedTeams.map((item) => item.id)
        : [];
      model.teams = teamArray;

      // Handle temperature range consistently - ensure all values are properly typed
      if (model.temperature_range === 'custom') {
        model.temp_range_is_custom = true;
        // For custom ranges, use the form field values directly
        // Special handling for 0 values - check if value is null/undefined/empty string rather than falsy check
        model.temperature_min =
          model.temperature_min !== null &&
          model.temperature_min !== undefined &&
          model.temperature_min !== ''
            ? Number(model.temperature_min)
            : null;
        model.temperature_max =
          model.temperature_max !== null &&
          model.temperature_max !== undefined &&
          model.temperature_max !== ''
            ? Number(model.temperature_max)
            : null;
        model.temperature_unit = model.temperature_unit || 'C';
      } else if (model.temperature_range === 'none') {
        // For "none" temperature range, set min/max to null
        model.temperature_min = null;
        model.temperature_max = null;
        model.temperature_unit = 'C';
        model.temp_range_is_custom = false;
      } else if (model.temperature_range) {
        // For predefined ranges, use the form field values if they exist, otherwise use the standard values
        if (model.temperature_min && model.temperature_max) {
          model.temperature_min = Number(model.temperature_min);
          model.temperature_max = Number(model.temperature_max);
          model.temperature_unit = model.temperature_unit || 'C';
          model.temp_range_is_custom = false;
        } else {
          // Look up the standard range values
          const rangeObj = tempratureRangeList.find(
            (x) => x.value === model.temperature_range
          );
          if (rangeObj) {
            model.temperature_min = rangeObj.min;
            model.temperature_max = rangeObj.max;
            model.temperature_unit = 'C';
            model.temp_range_is_custom = false;
          } else {
            // Fallback to temperatureObject if form values are not set and no standard range found
            model = { ...model, ...temperatureObject };
          }
        }
      }

      // Ensure all temperature values are consistently typed as numbers
      if (
        model.temperature_min !== null &&
        model.temperature_min !== undefined &&
        model.temperature_min !== ''
      ) {
        model.temperature_min = Number(model.temperature_min);
      }
      if (
        model.temperature_max !== null &&
        model.temperature_max !== undefined &&
        model.temperature_max !== ''
      ) {
        model.temperature_max = Number(model.temperature_max);
      }

      if (
        model.stage_of_development &&
        model?.stage_of_development !== 'Commercial'
      ) {
        model.commercial_value = null;
      }
      model.viability_window = !model?.viability_window
        ? null
        : model?.viability_window;

      submitProducts(model);
    }
  };

  const submitProducts = (model) => {
    setIsFormValid(true);
    showSpin();
    AddProducts(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (!data) return;

        if (!data?.data?.is_success) {
          message.error(data?.data?.message);
          return;
        }

        const finishUp = () => {
          message.success(data?.data?.message);
          const created = data?.data?.data?.created_products;
          handleFetchAllProduct(
            !id && created?.id
              ? { id: created.id, name: model.name }
              : undefined
          );
          handleCloseDrawer();
          setSelectedTeams([]);
          setPendingTimeOutOfRangeData(null);
        };

        // Add case with TOR staged in the sub-drawer: the product now has a
        // real id, so build the FormData from the staged values and send it
        const newProductId = id || data?.data?.data?.created_products?.id;
        if (!id && pendingTimeOutOfRangeData && newProductId) {
          const formData = buildTimeOutOfRangeFormData(
            pendingTimeOutOfRangeData.values,
            pendingTimeOutOfRangeData.documents,
            pendingTimeOutOfRangeData.removedDocumentIds,
            timeOutOfRangeEnabled
          );
          return SaveProductTimeOutOfRange(token, newProductId, formData)
            .then(() => finishUp())
            .catch((error) => {
              message.warning(
                'Product saved, but time out of range settings failed to save.'
              );
              unauthorize(error);
              finishUp();
            });
        }

        finishUp();
      })
      .catch((error) => {
        // Show detailed error message
        if (error?.data?.data?.errors) {
          const errors = error.data.data.errors;
          if (Array.isArray(errors) && errors.length > 0) {
            const firstError = errors[0];
            if (firstError.errors) {
              const errorMessages = Object.entries(firstError.errors)
                .map(
                  ([field, msgs]) =>
                    `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
                )
                .join('\n');
              message.error(`Validation errors:\n${errorMessages}`);
            }
          }
        } else if (error?.data?.data?.message) {
          const msg = error.data.data.message;
          const errorText =
            typeof msg === 'string' ? msg : msg?.text || 'An error occurred';
          message.error(errorText);
        } else if (error?.data?.message) {
          const msg = error.data.message;
          const errorText =
            typeof msg === 'string' ? msg : msg?.text || 'An error occurred';
          message.error(errorText);
        }
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  // Product search function for autocomplete
  const handleProductSearch = async (searchText) => {
    if (!fdaAutocompleteEnabled || !searchText || searchText.length < 2) {
      setProductSuggestions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await getProductAutocomplete(token, searchText);
      if (response?.data?.suggestions) {
        setProductSuggestions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setProductSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle product selection from autocomplete
  const handleProductSelect = async (value) => {
    form.setFieldsValue({ name: value });
    setSelectedProductName(value);
    setActualSelectedProductName(value); // Store the actually selected product name

    // Trigger autofill when a product is selected (only if autocomplete is enabled)
    if (fdaAutocompleteEnabled) {
      await handleProductAutofill(value);
    }
  };

  // Fetch FDA data for selected product
  const fetchFDAData = async () => {
    if (!actualSelectedProductName) {
      message.warning('Please select a product first');
      return;
    }

    setFdaLoading(true);
    try {
      const response = await getProductFDAInfo(
        token,
        actualSelectedProductName
      );
      if (response?.data?.success) {
        let fdaDataWithHighlights = response.data.data;

        // Use highlighted sections exactly as provided by the backend
        if (
          fdaDataWithHighlights.highlighted_sections &&
          fdaDataWithHighlights.relevant_info
        ) {
          const originalSections = fdaDataWithHighlights.highlighted_sections;

          // Convert string sections to proper format if needed, but don't filter
          const processedSections = [];

          originalSections.forEach((section, _index) => {
            if (typeof section === 'string') {
              // If it's a string, find it in the text and create indices
              const sectionText = section;
              const startIndex =
                fdaDataWithHighlights.relevant_info.indexOf(sectionText);

              if (startIndex !== -1) {
                processedSections.push({
                  text: sectionText,
                  start_index: startIndex,
                  end_index: startIndex + sectionText.length,
                });
              }
            } else if (
              section &&
              typeof section === 'object' &&
              section.start_index !== undefined
            ) {
              // If it's already an object with indices, use it as-is
              processedSections.push(section);
            }
          });

          fdaDataWithHighlights.highlighted_sections = processedSections;
        } else if (!fdaDataWithHighlights.highlighted_sections) {
          fdaDataWithHighlights.highlighted_sections = [];
        }

        setFdaData(fdaDataWithHighlights);
        setShowFDAData(true);
      } else {
        message.error('No FDA data found for this product');
      }
    } catch (error) {
      console.error('Error fetching FDA data:', error);
      message.error('Failed to fetch FDA data');
    } finally {
      setFdaLoading(false);
    }
  };

  // Function to clear all autofill data and reset form fields
  const clearAutofillData = () => {
    setAutofillData(null);
    setFdaData(null);
    setShowFDAData(false);
    setSelectedRequirements([]);
    setAvailableOptions(['Shock', 'Light', 'Tilt', 'Pressure', 'Humidity']);
    setShowDropdown(false);
    setTemperatureObject({
      temperature_max: '',
      temperature_min: '',
      temp_range_is_custom: false,
      temperature_unit: 'C',
    });
    setActualSelectedProductName('');
    setIsCustomRange(false);

    // Reset form fields that were autofilled including dropdowns
    form.setFieldsValue({
      product_type: undefined,
      project_id: '',
      temperature_range: undefined,
      temperature_min: '',
      temperature_max: '',
      temp_range_is_custom: false,
      temperature_unit: 'C',
      commercial_value: '',
      viability_window: '',
      shock: false,
      light: false,
      tilt: false,
      pressure: false,
      humidity: false,
      pressure_min: '',
      pressure_max: '',
      pressure_unit: 'psi',
      humidity_min: '',
      humidity_max: '',
    });
  };

  // Function to handle product autofill
  const handleProductAutofill = async (productName) => {
    if (!productName || productName.length < 2) {
      return;
    }

    setAutofillLoading(true);
    try {
      const response = await getProductAutofillInfo(token, productName);
      if (response?.data?.success) {
        const data = response.data.data;
        setAutofillData(data);

        // Convert product_type name to UUID if needed
        let productTypeId = data.product_type || '';

        // If productTypeId is not empty and looks like a name (not a UUID)
        if (
          productTypeId &&
          typeof productTypeId === 'string' &&
          !productTypeId.includes('-')
        ) {
          // Try to find it in the current productTypeDDL
          let matchingOption = productTypeDDL.find(
            (option) => option.name === productTypeId
          );

          if (matchingOption) {
            // Found in current list
            productTypeId = matchingOption.id;
          } else {
            // Not found in current list, fetch all types to find it
            await getProductsTypeDDL(token).then((types) => {
              if (types && types.length > 0) {
                const found = types.find((t) => t.name === productTypeId);
                if (found) {
                  productTypeId = found.id;
                  // Also update the productTypeDDL state
                  setProductTypeDDL(types);
                } else {
                  // Type not found in any list, clear it
                  productTypeId = '';
                }
              } else {
                productTypeId = '';
              }
            });
          }
        } else if (productTypeId && productTypeId.includes('-')) {
          // Already a UUID, keep it as is
          // (no change needed)
        } else {
          // Empty or invalid, clear it
          productTypeId = '';
        }

        // Process temperature range data to ensure consistency
        // Handle both field name formats: temperature_min/max and min_temperature/max_temperature
        let processedTemperatureMin =
          data.temperature_min !== undefined
            ? data.temperature_min
            : data.min_temperature;
        let processedTemperatureMax =
          data.temperature_max !== undefined
            ? data.temperature_max
            : data.max_temperature;
        let processedTemperatureUnit = data.temperature_unit || 'C';

        // Convert to numbers if they exist (including 0)
        // Explicitly handle 0 values by checking for undefined, null, or empty string
        processedTemperatureMin =
          processedTemperatureMin !== undefined &&
          processedTemperatureMin !== null &&
          processedTemperatureMin !== ''
            ? Number(processedTemperatureMin)
            : '';
        processedTemperatureMax =
          processedTemperatureMax !== undefined &&
          processedTemperatureMax !== null &&
          processedTemperatureMax !== ''
            ? Number(processedTemperatureMax)
            : '';

        // Determine temperature range based on min/max values
        let processedTemperatureRange = 'custom';
        let isCustomRange = true;

        if (processedTemperatureMin !== '' && processedTemperatureMax !== '') {
          // Use the utility function to determine if this matches a standard range
          const rangeInfo = determineTemperatureRange(
            processedTemperatureMin,
            processedTemperatureMax,
            processedTemperatureUnit
          );

          if (rangeInfo.type === 'standard') {
            processedTemperatureRange = rangeInfo.value;
            isCustomRange = false;
          } else if (rangeInfo.type === 'not_specified') {
            processedTemperatureRange = 'none';
            isCustomRange = false;
          } else {
            // It's a custom range - keep the min/max values
            processedTemperatureRange = 'custom';
            isCustomRange = true;
          }
        } else if (data.temperature_range) {
          // If we have a temperature_range string but no min/max values
          let tempRange = data.temperature_range;

          // If temperature_range contains the full label (like "15°C to 30°C (Ambient)"),
          // extract just the value (like "ambient")
          if (typeof tempRange === 'string') {
            // Check if it's a full label by looking for the pattern "°C to °C (Value)"
            const fullLabelMatch = tempRange.match(/^.*\(([^)]+)\)$/);
            if (fullLabelMatch) {
              tempRange = fullLabelMatch[1].toLowerCase();
            }

            // Try to find this range in our standard list
            const rangeObj = tempratureRangeList.find(
              (x) =>
                x.value === tempRange.toLowerCase() ||
                x.label.toLowerCase().includes(tempRange.toLowerCase())
            );

            if (rangeObj) {
              processedTemperatureRange = rangeObj.value;
              processedTemperatureMin = rangeObj.min;
              processedTemperatureMax = rangeObj.max;
              isCustomRange = false;
            }
          }
        }

        // Autofill the form with the processed data
        const formData = {
          name: data.name || '',
          project_id: data.project_id || '',
          viability_window: data.viability_window
            ? Number(data.viability_window)
            : null,
          temperature_range: processedTemperatureRange || null,
          temperature_min: processedTemperatureMin,
          temperature_max: processedTemperatureMax,
          temperature_unit: processedTemperatureUnit,
          temp_range_is_custom: isCustomRange,
          shock: data.shock || '',
          light: data.light || '',
          tilt: data.tilt || '',
          pressure_min: data.pressure_min || '',
          pressure_max: data.pressure_max || '',
          pressure_unit: data.pressure_unit || 'psi',
          humidity_min: data.humidity_min || '',
          humidity_max: data.humidity_max || '',
        };

        // Only add product_type if we have a valid UUID
        if (productTypeId && productTypeId !== '') {
          formData.product_type = productTypeId;
        }

        form.setFieldsValue(formData);

        // Update temperature object state
        const tempObject = {
          temperature_min: processedTemperatureMin,
          temperature_max: processedTemperatureMax,
          temperature_unit: processedTemperatureUnit,
          temp_range_is_custom: isCustomRange,
        };

        setTemperatureObject(tempObject);

        // Update custom range flag
        setIsCustomRange(isCustomRange);

        // Handle handling requirements
        const selectedFromBackend = REQUIREMENT_KEYS.filter((key) => {
          if (key === 'pressure') {
            return (
              (data.pressure_min !== undefined &&
                data.pressure_min !== null &&
                data.pressure_min !== '') ||
              (data.pressure_max !== undefined &&
                data.pressure_max !== null &&
                data.pressure_max !== '')
            );
          }

          if (key === 'humidity') {
            return (
              (data.humidity_min !== undefined &&
                data.humidity_min !== null &&
                data.humidity_min !== '') ||
              (data.humidity_max !== undefined &&
                data.humidity_max !== null &&
                data.humidity_max !== '')
            );
          }

          return (
            data[key] !== undefined && data[key] !== null && data[key] !== ''
          );
        });

        const selectedCapitalized = selectedFromBackend.map(
          (key) => key.charAt(0).toUpperCase() + key.slice(1)
        );
        setSelectedRequirements(selectedCapitalized);
        setShowDropdown(selectedCapitalized.length === 5);
        setAvailableOptions((prevOptions) =>
          prevOptions.filter((opt) => !selectedCapitalized.includes(opt))
        );
      } else {
        message.warning('No autofill data found for this product');
      }
    } catch (error) {
      console.error('Error fetching autofill data:', error);
      message.error('Failed to fetch autofill data');
    } finally {
      setAutofillLoading(false);
    }
  };

  // default values are set on drawer open
  useEffect(() => {
    form.setFieldsValue({
      teamRoles: [{ team: undefined, role: undefined }],
    });
  }, [form]);

  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [currentRequirement, setCurrentRequirement] = useState('');

  //This is the code for making assurity that pressure, humidity must have default value [0,100 ]
  useEffect(() => {
    const currentValue = id ? form.getFieldsValue() : null;
    if (currentRequirement === 'Pressure') {
      form.setFieldsValue({
        pressure_min:
          currentValue?.pressure_min || form.getFieldValue('pressure_min') || 0,
        pressure_max:
          currentValue?.pressure_max ||
          form.getFieldValue('pressure_max') ||
          100,
        pressure_unit:
          currentValue?.pressure_unit ||
          form.getFieldValue('pressure_unit') ||
          'psi',
      });
    }
    if (currentRequirement === 'Humidity') {
      form.setFieldsValue({
        humidity_min:
          currentValue?.humidity_min || form.getFieldValue('humidity_min') || 0,
        humidity_max:
          currentValue?.humidity_max ||
          form.getFieldValue('humidity_max') ||
          100,
      });
    }
  }, [currentRequirement, form, id]);

  useEffect(() => {
    if (selectedRequirements?.length > 0) {
      setCurrentRequirement(
        selectedRequirements[selectedRequirements.length - 1]
      );
    }
  }, [selectedRequirements]);

  const [availableOptions, setAvailableOptions] = useState([
    'Shock',
    'Light',
    'Tilt',
    'Pressure',
    'Humidity',
  ]);
  const [showDropdown, setShowDropdown] = useState(false);

  const baseStyle = {
    height: 36,
    width: '100%',
    borderRadius: 8,
    backgroundColor: 'var(--light-gray)',
    border: '0',
    color: 'var(--text-secondary)',
    paddingLeft: 12,
    paddingRight: 12,
    outline: 'none',
  };

  const [_selectKey, _setSelectKey] = useState(0);
  const renderRequirementField = (type, _index) => {
    const removeItem = (typeToRemove) => {
      // Remove by type instead of index
      setSelectedRequirements((prev) => prev.filter((t) => t !== typeToRemove));
      // Add back to available options
      setAvailableOptions((prev) => [...prev, typeToRemove].sort());

      // Clear form values
      switch (typeToRemove) {
        case 'Shock':
        case 'Light':
        case 'Tilt':
          form.setFieldsValue({ [typeToRemove.toLowerCase()]: null });
          break;
        case 'Pressure':
          form.setFieldsValue({
            pressure_min: null,
            pressure_max: null,
            pressure_unit: null,
          });
          break;
        case 'Humidity':
          form.setFieldsValue({
            humidity_min: null,
            humidity_max: null,
          });
          break;
        default:
          break;
      }
    };

    const renderSimpleInput = (_suffix) => (
      <Form.Item
        label={type}
        name={type.toLowerCase()}
        style={{ marginBottom: 16 }}
      >
        <div className={`requirement-input-style ${styles.requirementRow}`}>
          <Form.Item
            name={type.toLowerCase()}
            rules={[
              {
                pattern: /^(?:\d{1,6}|\d{0,5}\.\d{1,6})$/,
                message: 'Enter a valid number (max 6 digits total)',
              },
            ]}
            noStyle
          >
            <CustomInput
              placeholder='Enter value'
              height='36px'
              border='0px'
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
              suffixFontWeight='600'
              suffixColor='var(--text-primary)'
              suffix={type === 'Shock' ? 'G' : type === 'Light' ? 'lux' : 'Deg'}
            />
          </Form.Item>
          <span
            role='button'
            aria-label='Delete'
            className={styles.deleteAction}
            onClick={() => removeItem(type)}
          >
            <DeleteIcon color='var(--delete-icon-color)' />
          </span>
        </div>
      </Form.Item>
    );

    switch (type) {
      case 'Shock':
        return renderSimpleInput('G');
      case 'Light':
        return renderSimpleInput('lux');
      case 'Tilt':
        return renderSimpleInput('Deg');

      case 'Pressure':
        return (
          <Form.Item label='Pressure' style={{ marginBottom: 16 }}>
            <div className={`requirement-input-style ${styles.requirementRow}`}>
              <Form.Item
                name='pressure_min'
                dependencies={['pressure_max']}
                noStyle
                rules={[
                  {
                    pattern:
                      /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                    message: 'Min value must be between 0 and 100',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const max = getFieldValue('pressure_max');

                      // only run check if min has some value (including 0)
                      if (
                        value !== undefined &&
                        value !== null &&
                        value !== ''
                      ) {
                        if (max === undefined || max === null || max === '') {
                          return Promise.reject(
                            new Error(
                              'Max value is required when Min is entered'
                            )
                          );
                        }
                      }
                      if (isMinAboveMax(value, max)) {
                        return Promise.reject(new Error(MIN_ABOVE_MAX_MESSAGE));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <CustomInput
                  placeholder='0'
                  height='36px'
                  width={'102px'}
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
              <span>-</span>
              <Form.Item
                name='pressure_max'
                dependencies={['pressure_min']}
                noStyle
                rules={[
                  {
                    //pattern: /^(10(\.0{1,4})?|[1-9](\.\d{1,4})?)$/,
                    pattern:
                      /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                    message: 'Max value must be between 0 and 100',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const min = getFieldValue('pressure_min');

                      if (
                        value !== undefined &&
                        value !== null &&
                        value !== ''
                      ) {
                        if (min === undefined || min === null || min === '') {
                          return Promise.reject(
                            new Error(
                              'Min value is required when Max is entered'
                            )
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <CustomInput
                  placeholder='100'
                  height='36px'
                  width={'102px'}
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
              <Form.Item name='pressure_unit' noStyle initialValue='psi'>
                <Select
                  options={[
                    { label: 'psi', value: 'psi' },
                    { label: 'mbar', value: 'mbar' },
                  ]}
                  style={{ ...baseStyle, width: 120 }}
                  dropdownStyle={{ borderRadius: 8 }}
                />
              </Form.Item>
              <span
                role='button'
                aria-label='Delete'
                className={styles.deleteAction}
                onClick={() => removeItem(type)}
              >
                <DeleteIcon color='var(--delete-icon-color)' />
              </span>
            </div>
          </Form.Item>
        );

      case 'Humidity':
        return (
          <>
            <Form.Item label='Humidity' style={{ marginBottom: 16 }}>
              <div
                className={`requirement-input-style ${styles.requirementRow}`}
              >
                <Form.Item
                  name='humidity_min'
                  dependencies={['humidity_max']}
                  noStyle
                  rules={[
                    {
                      pattern:
                        /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                      message: 'Min value must be between 0 and 100',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const max = getFieldValue('humidity_max');

                        // only run check if min has some value (including 0)
                        if (
                          value !== undefined &&
                          value !== null &&
                          value !== ''
                        ) {
                          if (max === undefined || max === null || max === '') {
                            return Promise.reject(
                              new Error(
                                'Max value is required when Min is entered'
                              )
                            );
                          }
                        }
                        if (isMinAboveMax(value, max)) {
                          return Promise.reject(
                            new Error(MIN_ABOVE_MAX_MESSAGE)
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder='0'
                    height='36px'
                    width={'102px'}
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    //placeholderColor="var(--text-secondary)"
                    lineHeight='38px'
                  />
                </Form.Item>
                <span>-</span>
                <Form.Item
                  name='humidity_max'
                  dependencies={['humidity_min']}
                  noStyle
                  rules={[
                    {
                      pattern: /^(100(\.0{1,4})?|[1-9][0-9]?(\.\d{1,4})?)$/,
                      message: 'Max value between 1 and 100',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const min = getFieldValue('humidity_min');

                        if (
                          value !== undefined &&
                          value !== null &&
                          value !== ''
                        ) {
                          if (min === undefined || min === null || min === '') {
                            return Promise.reject(
                              new Error(
                                'Min value is required when Max is entered'
                              )
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder='100'
                    height='36px'
                    width={'102px'}
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
                <Form.Item noStyle>
                  <CustomInput
                    placeholder='%'
                    disabled
                    style={{ ...baseStyle, width: 120 }} // Match Pressure unit width
                  />
                </Form.Item>
                <span
                  role='button'
                  aria-label='Delete'
                  className={styles.deleteAction}
                  onClick={() => removeItem(type)}
                >
                  <DeleteIcon color='var(--delete-icon-color)' />
                </span>
              </div>
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };
  const onToggleChange = (checkedOrEvent) => {
    const checked =
      typeof checkedOrEvent === 'boolean'
        ? checkedOrEvent
        : checkedOrEvent?.target?.checked;
    setTimeOutOfRangeEnabled(!!checked);

    // Add case: turning the toggle back off after staging TOR data means
    // there's nothing left to save — drop the stale stage.
    if (!checked) {
      setPendingTimeOutOfRangeData(null);
    }
  };

  const handleToggleTimeOutDrawer = () => {
    if (!timeOutOfRangeEnabled) return;

    // Add case: the product doesn't exist yet, so require only the
    // temperature range (TOR's own dependency, same check that gates the
    // toggle itself) before letting the user into the TOR sub-drawer —
    // not the whole form's required fields. Reuses hasRequiredFieldsFilled
    // rather than form.validateFields() since the custom-range min/max
    // completeness check lives on an unnamed Form.Item (validated via a
    // custom validator reading getFieldValue), not on fields validateFields
    // can target directly by name.
    if (!id) {
      if (!hasRequiredFieldsFilled) {
        message.error(
          'Please select a temperature range before adding time out of range.'
        );
        return;
      }
      setShowTimeOutOfRangeDrawer(true);
      return;
    }

    setShowTimeOutOfRangeDrawer(true);
  };

  return (
    <>
      {!showTimeOutOfRangeDrawer && (
        <Form
          form={form}
          name='settingsProductForm'
          layout='vertical'
          onFinish={onFinish}
          autoComplete='off'
          className='drawer-children-h flex flex-col justify-between'
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          initialValues={{
            name: '',
            product_type: null,
            commercial_value: '',
            temperature_range: null,
            viability_window: null,
            temperature_min: '',
            temperature_max: '',
            temperature_unit: 'C',
            pressure_unit: 'psi',
          }}
        >
          {showFDAData ? (
            // FDA Data View
            <>
              <div className={styles.header}>
                <div className={styles.backRow}>
                  <span
                    onClick={() => setShowFDAData(false)}
                    className={styles.backLink}
                  >
                    ← Back to edit product
                  </span>
                </div>

                <CustomTitle
                  text='FDA data'
                  fontSize={20}
                  fontWeight='700'
                  lineHeight='32px'
                  color='var(--text-primary)'
                  marginBottom={0}
                />
              </div>

              <div className={styles.fdaBody}>
                <div className={styles.fdaTitleWrap}>
                  <CustomTitle
                    text={`${actualSelectedProductName} - Handling requirements`}
                    fontSize={16}
                    fontWeight='600'
                    color='#333'
                    marginBottom={16}
                  />
                </div>

                {fdaLoading ? (
                  <div className={styles.fdaLoading}>
                    <div>Loading FDA data...</div>
                  </div>
                ) : fdaData && fdaData.relevant_info ? (
                  <div className={styles.fdaInfo}>
                    <HighlightedText
                      text={fdaData.relevant_info}
                      highlightedSections={fdaData.highlighted_sections}
                      highlightStyle={{
                        color: '#1565c0',
                        fontWeight: '600',
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.fdaEmpty}>
                    No FDA handling requirements found for this product.
                  </div>
                )}
              </div>

              <div className={styles.fdaFooter}>
                <div className={styles.fdaButtons}>
                  <CustomButton
                    text={'Return'}
                    width={'100%'}
                    height={48}
                    borderRadius={8}
                    bgColor='#000'
                    color='#fff'
                    border='0px'
                    fontSize={16}
                    fontWeight={600}
                    onClick={() => setShowFDAData(false)}
                  />
                  <CustomButton
                    text={'Cancel'}
                    width={'100%'}
                    height={48}
                    borderRadius={8}
                    bgColor='transparent'
                    color='var(--text-primary)'
                    border='0px'
                    fontSize={16}
                    fontWeight={600}
                    onClick={handleCloseDrawer}
                  />
                </div>
              </div>
            </>
          ) : (
            // Regular Form View
            <>
              <div className={'drawer-title-header'}>
                <Flex align='center' gap={8}>
                  {showBackButton && (
                    <button
                      type='button'
                      aria-label='Back'
                      onClick={handleCloseDrawer}
                      className={styles.backButton}
                    >
                      <ArrowLeftOutlined style={{ fontSize: 14 }} />
                    </button>
                  )}
                  <CustomTitle
                    text={id ? 'Edit product' : 'Add product'}
                    fontSize={20}
                    fontWeight='700'
                    lineHeight='32px'
                    color='var(--text-primary)'
                    marginBottom={0}
                  />
                </Flex>
              </div>

              <div className={`custom-scrollbar ${styles.formBody}`}>
                <Row gutter={[16, 16]}>
                  {/* <Col span={24}>
              <CustomParagraph
                text={`Product info`}
                fontSize={15}
                fontWeight={600}
                color="var(--text-primary)"
                lineHeight={"20px"}
                marginBottom={4}
              />
            </Col> */}
                  {/* product name  */}
                  <Col span={24}>
                    {/* Toggle positioned above the input field */}
                    <div className={styles.nameRow}>
                      <span className={styles.nameLabel}>Name</span>
                      {!hideFdaDatabase && (
                        <div className={styles.fdaToggle}>
                          <span className={styles.fdaToggleLabel}>
                            Use FDA database
                          </span>
                          <Switch
                            checked={useAutocomplete}
                            onChange={(checked) => {
                              setUseAutocomplete(checked);
                              if (!checked) {
                                setProductSuggestions([]);
                                setSelectedProductName('');
                                form.setFieldsValue({ name: '' });
                                clearAutofillData();
                              }
                            }}
                            size='small'
                            className='custom-switch'
                          />
                        </div>
                      )}
                    </div>

                    <Form.Item
                      className='custom-input form-input-style'
                      name='name'
                      rules={[
                        {
                          required: true,
                          message: 'Please enter product name',
                        },
                        {
                          type: 'string',
                          min: 3,
                          message:
                            'Product name must be at least 3 characters long',
                        },
                      ]}
                      style={{ marginBottom: '0px' }}
                    >
                      {fdaAutocompleteEnabled ? (
                        <div className='autocomplete-wrapper'>
                          <AutoComplete
                            value={selectedProductName}
                            options={productSuggestions.map((suggestion) => ({
                              value: suggestion,
                              label: (
                                <div className={styles.suggestion}>
                                  <span>{suggestion}</span>
                                  <span className={styles.suggestionHint}>
                                    (Click to Autofill)
                                  </span>
                                </div>
                              ),
                            }))}
                            onSearch={handleProductSearch}
                            onSelect={handleProductSelect}
                            onChange={(value) => {
                              setSelectedProductName(value || '');
                              form.setFieldsValue({ name: value || '' });
                            }}
                            onKeyDown={(e) => {
                              // Clear autofill data when backspace is pressed
                              if (e.key === 'Backspace' && autofillData) {
                                clearAutofillData();
                              }
                            }}
                            onClear={() => {
                              // Clear autofill data when the clear button (x) is clicked
                              if (autofillData) {
                                clearAutofillData();
                              }
                              setSelectedProductName('');
                              form.setFieldsValue({ name: '' });
                            }}
                            placeholder='Enter value'
                            allowClear
                            loading={searchLoading || autofillLoading}
                            className='product-name-autocomplete'
                            style={{
                              width: '100%',
                              height: '36px',
                              backgroundColor: 'var(--light-gray)',
                              borderRadius: '8px',
                              border: 'none',
                            }}
                          />
                        </div>
                      ) : (
                        <CustomInput
                          placeholder='Enter value'
                          height='36px'
                          border='0px'
                          bgColor='var(--light-gray)'
                          textColor='var(--text-secondary)'
                          borderRadius='8px'
                          placeholderColor='var(--text-secondary)'
                          lineHeight='38px'
                          onChange={(e) =>
                            setSelectedProductName(e.target.value || '')
                          }
                        />
                      )}
                    </Form.Item>

                    {/* Autofill Status Indicators */}
                    {fdaAutocompleteEnabled && autofillLoading && (
                      <div className={styles.autofillStatus}>
                        Loading product information...
                      </div>
                    )}

                    {/* FDA Data Link */}
                    {fdaAutocompleteEnabled && actualSelectedProductName && (
                      <div className={styles.fdaLinkWrap}>
                        <span onClick={fetchFDAData} className={styles.fdaLink}>
                          See FDA data →
                        </span>
                      </div>
                    )}
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      name='identifier'
                      label='Identifier'
                      whiteSpace
                      rules={[
                        {
                          type: 'string',
                          min: 3,
                          message:
                            'Product identifier must be at least 3 characters long',
                        },
                        {
                          type: 'string',
                          max: 50,
                          message:
                            'Product identifier cannot exceed 50 characters.',
                        },
                        {
                          whitespace: true,
                          message: 'Only whitespace is not allowed.',
                        },
                      ]}
                      style={{ marginBottom: '0px' }}
                    >
                      <CustomInput
                        placeholder='Enter value'
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                      />
                    </Form.Item>
                  </Col>
                  {/* product type  */}
                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label='Product type'
                      name='product_type'
                      style={{ marginBottom: '0px' }}
                      rules={[
                        { required: true, message: 'Please select value' },
                      ]}
                    >
                      <CustomDynamicSelect
                        // suffixIcon={<DownOutlined style={{ color: "var(--text-secondary)" }} />}
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                        options={productTypeDDL}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label='Project ID'
                      name='project_id'
                      style={{ marginBottom: '0px' }}
                    >
                      <CustomInput
                        placeholder='Enter value'
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '0px' }} />
                  </Col>
                  <Col span={24}>
                    <CustomParagraph
                      noText='-'
                      text={`Stability & Handling Requirements`}
                      fontSize={15}
                      fontWeight={600}
                      color='var(--text-primary)'
                      lineHeight={'20px'}
                      marginBottom={0}
                    />
                  </Col>
                  {/* Transit viability window */}
                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label='Transit viability window'
                      name='viability_window'
                      rules={[
                        //{ required: true, message: "Please enter value" },
                        {
                          pattern: /^\d{1,5}$/,
                          message: 'Enter up to 5 digits only!',
                        },
                      ]}
                      style={{ marginBottom: '0px' }}
                    >
                      <CustomInput
                        placeholder='Enter value'
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                        suffixFontWeight='600'
                        suffixColor='var(--text-primary)'
                        suffix='hr'
                      />
                    </Form.Item>
                  </Col>
                  {/* Temperature range */}
                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label='Temperature range'
                      name='temperature_range'
                      rules={[
                        { required: true, message: 'Please select value' },
                      ]}
                      style={{ marginBottom: '0px' }}
                    >
                      <CustomSelect
                        // suffixIcon={<DownOutlined style={{ color: "var(--text-secondary)" }} />}
                        placeholder='Select'
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                        onChange={handleTemperatureRangeChange}
                        options={tempratureRangeList}
                      />
                    </Form.Item>
                  </Col>
                  {/* Custom Temperature Range */}
                  {isCustomRange && (
                    <>
                      <Col span={24}>
                        <Form.Item
                          className='custom-input form-input-style'
                          label='Custom Temperature Range'
                          name=''
                          style={{ marginBottom: '0px', width: '100%' }}
                          dependencies={[
                            'temperature_min',
                            'temperature_max',
                            'temperature_unit',
                          ]}
                          validateTrigger='onSubmit'
                          rules={[
                            {
                              validator: (_, _value) => {
                                const customMin =
                                  form.getFieldValue('temperature_min');
                                const customMax =
                                  form.getFieldValue('temperature_max');
                                const customUnit =
                                  form.getFieldValue('temperature_unit');
                                // Properly check for null/undefined/empty string but allow zero values
                                if (
                                  customMin === null ||
                                  customMin === undefined ||
                                  customMin === '' ||
                                  customMax === null ||
                                  customMax === undefined ||
                                  customMax === '' ||
                                  !customUnit
                                ) {
                                  return Promise.reject(
                                    new Error(
                                      'Please fill in all custom temperature fields.'
                                    )
                                  );
                                }
                                if (isMinAboveMax(customMin, customMax)) {
                                  return Promise.reject(
                                    new Error(MIN_ABOVE_MAX_MESSAGE)
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Row
                            gutter={[8, 0]}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Col span={16}>
                              <div
                                className={`w-full flex ${styles.customRange}`}
                              >
                                <Form.Item
                                  className='custom-input'
                                  name='temperature_min'
                                  style={{ width: '98%' }}
                                >
                                  <CustomInput
                                    placeholder='3'
                                    height='36px'
                                    width={'100%'}
                                    border='0px'
                                    bgColor='var(--light-gray)'
                                    textColor='var(--text-secondary)'
                                    borderRadius='8px'
                                    placeholderColor='var(--text-secondary)'
                                    lineHeight='38px'
                                    onChange={(e) =>
                                      setTemperatureObject((prev) => ({
                                        ...prev,
                                        temperature_min: e.target.value,
                                      }))
                                    }
                                  />
                                </Form.Item>

                                <span className='mx-2'>-</span>

                                <Form.Item
                                  name='temperature_max'
                                  style={{ width: '98%' }}
                                >
                                  <CustomInput
                                    placeholder='13'
                                    height='36px'
                                    width={'100%'}
                                    border='0px'
                                    bgColor='var(--light-gray)'
                                    textColor='var(--text-secondary)'
                                    borderRadius='8px'
                                    placeholderColor='var(--text-secondary)'
                                    lineHeight='38px'
                                    onChange={(e) =>
                                      setTemperatureObject((prev) => ({
                                        ...prev,
                                        temperature_max: e.target.value,
                                      }))
                                    }
                                  />
                                </Form.Item>
                              </div>
                            </Col>

                            <Col span={8}>
                              <Form.Item name='temperature_unit'>
                                <CustomSelect
                                  // suffixIcon={<DownOutlined style={{ color: "var(--text-secondary)" }} />}
                                  placeholder='Temperature unit'
                                  width='100%'
                                  height='36px'
                                  border='0px'
                                  bgColor='var(--light-gray)'
                                  textColor='var(--text-secondary)'
                                  borderRadius='8px'
                                  options={[
                                    { value: 'C', label: '°C' },
                                    { value: 'F', label: '°F' },
                                  ]}
                                  onChange={(value) =>
                                    setTemperatureObject((prev) => ({
                                      ...prev,
                                      temperature_unit: value,
                                    }))
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {/* Time out of range (Temperature GxP) */}
                  {TEMPERATURE_GXP_ENABLED && (
                    <Col span={24}>
                      <Flex
                        align='center'
                        justify='space-between'
                        className='add-time-out-of-range-text'
                      >
                        <span className='zl-heading-12'>
                          Add time out of range
                        </span>
                        <Flex align='center' gap={12}>
                          <Tooltip
                            title={
                              canEnableTimeOutOfRange
                                ? undefined
                                : TOR_REQUIRED_FIELD_MESSAGE
                            }
                          >
                            {/* disabled-row sets pointer-events: none, which would also
                                block the Tooltip's own hover listeners — this extra span
                                keeps pointer events so the tooltip still triggers. */}
                            <span className={styles.inlineBlock}>
                              <span
                                onClick={() => handleToggleTimeOutDrawer()}
                                className={`zl-heading-12 cursor-pointer ${timeOutOfRangeEnabled ? '' : 'disabled-row'}`}
                              >
                                Edit
                              </span>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              canEnableTimeOutOfRange
                                ? undefined
                                : TOR_REQUIRED_FIELD_MESSAGE
                            }
                          >
                            {/* Same pointer-events reasoning as above — wrap so the
                                tooltip still triggers while the switch is disabled. */}
                            <span className={styles.inlineBlock}>
                              <ToggleSwitch
                                checked={timeOutOfRangeEnabled}
                                onChange={onToggleChange}
                                disabled={!canEnableTimeOutOfRange}
                              />
                            </span>
                          </Tooltip>
                        </Flex>
                      </Flex>
                    </Col>
                  )}

                  {/* Add more required fields here */}
                  {/* rendering fields */}
                  {[...selectedRequirements].map((item, index) => (
                    <Col span={24} key={item}>
                      {renderRequirementField(item, index)}
                    </Col>
                  ))}

                  {/* show add requirement and dropdown  */}
                  {showDropdown ? (
                    <>
                      {availableOptions?.length > 0 && (
                        <Form.Item
                          label='Add requirement'
                          //name="requirement"
                          style={{
                            marginBottom: 0,
                            width: '100%',
                            padding: '0px 8px',
                          }}
                          labelCol={{ span: 24 }} // makes the label appear above the select
                        >
                          <div className={styles.addRequirementRow}>
                            <Select
                              key={_selectKey}
                              style={{
                                backgroundColor: 'var(--light-gray)',
                                borderRadius: '8px',
                                height: '36px',
                                // margin: "0 8px",
                                //width: "102px",
                              }}
                              placeholder='Select'
                              onChange={(value) => {
                                setSelectedRequirements([
                                  ...selectedRequirements,
                                  value,
                                ]);
                                setAvailableOptions(
                                  availableOptions.filter(
                                    (item) => item !== value
                                  )
                                );
                                setShowDropdown(false);
                              }}
                              options={availableOptions.map((item) => ({
                                label: item,
                                value: item,
                              }))}
                            />
                            <span
                              role='button'
                              aria-label='Delete'
                              className={styles.deleteAction}
                              onClick={() => setShowDropdown(false)}
                            >
                              <DeleteIcon color='var(--delete-icon-color)' />
                            </span>
                          </div>
                        </Form.Item>
                      )}{' '}
                    </>
                  ) : (
                    <>
                      {availableOptions?.length !== 0 && (
                        <div className={styles.addRequirementWrap}>
                          <AddItemButton
                            marginTop='0px'
                            fontSize='12px'
                            fontWeight='600'
                            width='28px'
                            height='28px'
                            text={'Add requirement'}
                            onClick={() => setShowDropdown(true)}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {/* Add more required fields END here */}
                  {/* teams  */}
                  {hasValidTeams && (
                    <>
                      <Col span={24}>
                        <Divider style={{ margin: '0' }} />
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          className='custom-input form-input-style'
                          label={`${themeT('team', theme.themeType)}s with access`}
                          name='teams'
                          rules={[
                            { required: true, message: 'Please select value' },
                          ]}
                        >
                          <TeamsSelector
                            teamsOptions={allTeams}
                            selectedTeams={selectedTeams}
                            onChange={setSelectedTeams}
                            formState={id ? 'edit' : 'add'}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                {/* form bottom buttons  */}
                <div className={'drawer-custom-footer'}>
                  <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
                    <CustomButton
                      text={'Save'}
                      width={'100%'}
                      height={48}
                      borderRadius={8}
                      bgColor={isFormValid ? '#F3F3F3' : '#000'}
                      color={isFormValid ? '#A6A6A6' : '#fff'}
                      border='0px'
                      fontSize={16}
                      fontWeight={600}
                      htmlType='submit'
                      isFormValid={isFormValid}
                    />
                    <CustomButton
                      key='button1'
                      width={'100%'}
                      onClick={() => handleCloseDrawer()}
                      text={'Cancel'}
                      bgColor='transparent'
                      color='black'
                      fontSize='16px'
                      fontWeight='600'
                      boxShadow='none'
                      height='48px'
                    />
                  </Row>
                </div>
              </div>
            </>
          )}
        </Form>
      )}

      {TEMPERATURE_GXP_ENABLED && showTimeOutOfRangeDrawer && (
        <TimeOutOfRangeDrawer
          timeOutOfRangeData={timeOutOfRangeData}
          open={showTimeOutOfRangeDrawer}
          onClose={() => setShowTimeOutOfRangeDrawer(false)}
          onSaveSuccess={() => {
            GetProductTimeOutOfRange(token, id)
              .then((res) => {
                const data = getTimeOutOfRangePayload(res);
                setTimeOutOfRangeData(data);
                setTimeOutOfRangeEnabled(!!data?.is_enabled);
              })
              .catch((error) => unauthorize(error));
          }}
          productId={id}
          token={token}
          temperatureMin={temperatureObject.temperature_min}
          temperatureMax={temperatureObject.temperature_max}
          temperatureUnit={temperatureObject.temperature_unit}
          timeOutOfRangeEnabled={timeOutOfRangeEnabled}
          currentUserName={decodedUserToken()?.name}
          unauthorize={unauthorize}
          onStageForCreate={setPendingTimeOutOfRangeData}
          stagedData={pendingTimeOutOfRangeData}
        />
      )}
    </>
  );
};

export default SettingsProductDrawerForm;
